import { useState, useEffect, useCallback } from 'react';
import { getSubcontractor } from '../api/subcontractors';
import { getApiErrorMessage } from '@/lib/api-error';
import type { SubcontractorDto } from '../types';

/**
 * Hook for fetching a single subcontractor by ID
 */
export function useSubcontractor(id: number | undefined) {
  const [data, setData] = useState<SubcontractorDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubcontractor = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const subcontractor = await getSubcontractor(id);
      setData(subcontractor);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch subcontractor'));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSubcontractor();
  }, [fetchSubcontractor]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSubcontractor,
  };
}
